import type { MoveAction } from '../game/world';

export class KeyboardInput {
  readonly #pressed = new Set<string>();
  readonly #leftPointers = new Set<number>();
  readonly #rightPointers = new Set<number>();

  constructor(
    private readonly leftButton?: HTMLButtonElement,
    private readonly rightButton?: HTMLButtonElement,
  ) {}

  start(): void {
    window.addEventListener('keydown', this.#onKeyDown);
    window.addEventListener('keyup', this.#onKeyUp);
    window.addEventListener('blur', this.#onBlur);
    this.#bindTouchButton(this.leftButton, this.#leftPointers);
    this.#bindTouchButton(this.rightButton, this.#rightPointers);
  }
  stop(): void {
    window.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('blur', this.#onBlur);
    this.#unbindTouchButton(this.leftButton);
    this.#unbindTouchButton(this.rightButton);
    this.#onBlur();
  }
  movement(): MoveAction {
    const right = this.#pressed.has('ArrowRight') || this.#pressed.has('KeyD') || this.#rightPointers.size > 0;
    const left = this.#pressed.has('ArrowLeft') || this.#pressed.has('KeyA') || this.#leftPointers.size > 0;
    return { x: Number(right) - Number(left) };
  }
  readonly #onKeyDown = (event: KeyboardEvent): void => {
    if (isTypingTarget(event.target)) return;
    if (['ArrowLeft', 'ArrowRight', 'KeyA', 'KeyD'].includes(event.code)) event.preventDefault();
    this.#pressed.add(event.code);
  };
  readonly #onKeyUp = (event: KeyboardEvent): void => { this.#pressed.delete(event.code); };
  readonly #onBlur = (): void => {
    this.#pressed.clear();
    this.#leftPointers.clear();
    this.#rightPointers.clear();
  };
  readonly #onPointerDown = (event: PointerEvent): void => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    button.setPointerCapture(event.pointerId);
    this.#pointersFor(button).add(event.pointerId);
  };
  readonly #onPointerEnd = (event: PointerEvent): void => {
    const button = event.currentTarget;
    if (button instanceof HTMLButtonElement) this.#pointersFor(button).delete(event.pointerId);
  };
  #pointersFor(button: HTMLButtonElement): Set<number> {
    return button === this.leftButton ? this.#leftPointers : this.#rightPointers;
  }
  #bindTouchButton(button: HTMLButtonElement | undefined, pointers: Set<number>): void {
    if (!button) return;
    pointers.clear();
    button.addEventListener('pointerdown', this.#onPointerDown);
    button.addEventListener('pointerup', this.#onPointerEnd);
    button.addEventListener('pointercancel', this.#onPointerEnd);
    button.addEventListener('lostpointercapture', this.#onPointerEnd);
  }
  #unbindTouchButton(button: HTMLButtonElement | undefined): void {
    if (!button) return;
    button.removeEventListener('pointerdown', this.#onPointerDown);
    button.removeEventListener('pointerup', this.#onPointerEnd);
    button.removeEventListener('pointercancel', this.#onPointerEnd);
    button.removeEventListener('lostpointercapture', this.#onPointerEnd);
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}
